/**
 * TrainerLayout — mobile-first layout for users with role=Formateur.
 * - Top bar with branding + user menu
 * - Bottom navigation bar (5 main tabs) on mobile
 * - Side rail on desktop (≥1024px)
 *
 * Vague 99.5: tabs are filtered by the trainer's `extranet_acl.tabs` map so
 * the admin can hide entire sections from a freelance.
 */
import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Home, Calendar, ClipboardCheck, Star, User, LogOut, FolderOpen } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/client';
import Logo from '../../components/Logo';

const ALL_TABS = [
  { to: '/trainer', key: 'home', label: 'Accueil', icon: Home, end: true, testid: 'trainer-nav-home' },
  { to: '/trainer/sessions', key: 'sessions', label: 'Sessions', icon: Calendar, testid: 'trainer-nav-sessions' },
  { to: '/trainer/emargements', key: 'emargements', label: 'Émargements', icon: ClipboardCheck, testid: 'trainer-nav-emargements' },
  { to: '/trainer/evaluations', key: 'evaluations', label: 'Évaluations', icon: Star, testid: 'trainer-nav-evaluations' },
  { to: '/trainer/documents', key: 'documents', label: 'Documents', icon: FolderOpen, testid: 'trainer-nav-documents' },
  { to: '/trainer/profil', key: 'profil', label: 'Profil', icon: User, testid: 'trainer-nav-profil' },
];

export default function TrainerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState(ALL_TABS.filter((t) => t.key !== 'documents'));
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/me/trainer').then(({ data }) => {
      if (cancelled) return;
      const aclTabs = (data?.extranet_acl?.tabs) || {};
      // Default each tab to true if the ACL doesn't mention it
      const visible = ALL_TABS.filter((t) => aclTabs[t.key] !== false && (t.key !== 'documents' || aclTabs.documents === true));
      setTabs(visible);
    }).catch((e) => {
      if (e?.response?.status === 403) {
        setAccessError(e.response.data?.detail || "Accès suspendu");
      }
    });
    return () => { cancelled = true; };
  }, []);

  const onLogout = async () => {
    try { await logout(); } catch (e) { console.warn("[logout]", e); }
    navigate('/login', { replace: true });
  };

  if (accessError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#F6F7FB]" data-testid="trainer-access-blocked">
        <div className="soft-card p-6 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-[#FEE2E2] text-[#B91C1C] flex items-center justify-center mx-auto mb-3">
            <LogOut className="w-5 h-5" />
          </div>
          <h2 className="text-[16px] font-bold text-[#1A1B2E] mb-2">Accès indisponible</h2>
          <p className="text-[13px] text-[#6B6C85] mb-4">{accessError}</p>
          <button
            onClick={onLogout}
            data-testid="trainer-blocked-logout"
            className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] pb-[76px] lg:pb-0 lg:pl-[72px]">
      {/* Top bar */}
      <header className="sticky top-0 z-20 h-14 bg-white border-b border-[#EFF0F6] flex items-center justify-between px-4 lg:px-6" data-testid="trainer-topbar">
        <div className="flex items-center gap-2">
          <Logo size={32} />
          <div className="leading-tight">
            <div className="text-[14px] font-bold" style={{ fontFamily: "'Satoshi','Plus Jakarta Sans',sans-serif", letterSpacing: '-0.02em' }}>
              <span className="text-[#4F46E5]">OF</span><span className="text-[#0F172A]">Manager</span>
            </div>
            <div className="text-[10.5px] text-[#8E8FA5] -mt-0.5">Espace formateur</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right hidden sm:block">
            <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate max-w-[180px]">{user?.name || user?.email}</div>
            <div className="text-[10.5px] text-[#8E8FA5]">{user?.org_name || ''}</div>
          </div>
          <div
            className="w-9 h-9 rounded-full brand-gradient flex items-center justify-center text-white text-[12px] font-bold"
            data-testid="trainer-avatar"
          >
            {(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}
          </div>
          <button
            onClick={onLogout}
            data-testid="trainer-logout"
            title="Se déconnecter"
            className="w-9 h-9 rounded-lg hover:bg-[#F3F4F8] flex items-center justify-center text-[#6B6C85] transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Desktop side rail */}
      <nav className="hidden lg:flex flex-col fixed left-0 top-14 bottom-0 w-[72px] bg-white border-r border-[#EFF0F6] py-3 items-center gap-1 z-10" data-testid="trainer-side-rail">
        {tabs.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) => `w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${isActive ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#8E8FA5] hover:bg-[#FAFAFE]'}`}
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Main content */}
      <main className="px-4 py-4 lg:px-8 lg:py-6 max-w-3xl mx-auto" data-testid="trainer-main">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-20 bg-white border-t border-[#EFF0F6] lg:hidden flex items-stretch h-[64px]" data-testid="trainer-bottom-nav">
        {tabs.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`${testid}-bottom`}
            className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${isActive ? 'text-[#4F46E5]' : 'text-[#8E8FA5]'}`}
          >
            <Icon className={`w-[18px] h-[18px] ${''}`} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
